import { Card } from "@/components/ui/card";
import { Building2, Smartphone, Package, DoorOpen, Users, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "@/hooks/use-toast";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const services = [
  {
    icon: Building2,
    title: "Complete Gym Design & Construction",
    description: "Full-cycle design, engineering, CNC manufacturing, and professional installation of climbing walls and bouldering structures."
  },
  {
    icon: DoorOpen,
    title: "Gym Automation Systems",
    description: "Modern turnstile systems, access control, and automated entry solutions to streamline your gym operations."
  },
  {
    icon: Smartphone,
    title: "Client Management Software",
    description: "Complete software solutions including booking systems, membership management apps, and client engagement platforms."
  },
  {
    icon: Package,
    title: "Climbing Hold Supply",
    description: "High-quality climbing holds and hardware delivery, ensuring your walls are equipped with the best materials."
  },
  {
    icon: Users,
    title: "Facility Planning & Design",
    description: "Expert consultation on reception areas, changing rooms, lounge spaces, and complete facility layout optimization."
  },
  {
    icon: Wrench,
    title: "Ongoing Support & Maintenance",
    description: "Comprehensive after-sales support, maintenance services, and technical assistance to keep your facility running smoothly."
  }
];

const formSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  phone: z.string().trim().min(1, "Phone is required").max(50, "Phone must be less than 50 characters"),
  message: z.string().trim().min(1, "Message is required").max(1000, "Message must be less than 1000 characters"),
});

const Services = () => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: values
      });

      if (error) throw error;

      toast({
        title: "Message sent!",
        description: "Thank you for contacting us. We'll get back to you soon.",
      });
      
      form.reset();
      setOpen(false);
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="services" className="py-20 bg-background relative overflow-hidden">
      {/* Decorative Background Element */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary/3 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 max-w-5xl mx-auto animate-fade-in">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-display text-foreground mb-6">
            CLIMBING GYM CONSTRUCTION SERVICES
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Turnkey climbing gym builder - Complete bouldering gym construction, design, and installation services from concept to completion
          </p>
        </div>
      </div>
      
      {/* Main Service - Full Width */}
      <div className="mb-12 px-4 sm:px-6 lg:px-8 animate-fade-in delay-100">
        <div className="bg-gradient-hero text-white p-10 lg:p-14 rounded-3xl border-2 border-primary relative overflow-hidden group hover:shadow-2xl transition-all duration-500 hover:scale-[1.02]">
          {/* Animated Background Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700" />
          <div className="absolute -bottom-10 -left-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl group-hover:scale-125 transition-transform duration-700" />
          
          <div className="relative z-10 max-w-7xl mx-auto text-center">
            <div className="mb-6 flex flex-col items-center">
              <div className="flex items-center gap-4 mb-4 justify-center">
                <div className="p-5 bg-primary/20 rounded-2xl group-hover:rotate-6 transition-transform duration-300">
                  <Building2 className="w-14 h-14 text-white" />
                </div>
                <div>
                  <h3 className="text-3xl lg:text-5xl font-display leading-tight">
                    Complete Climbing Gym Design & Construction
                  </h3>
                </div>
              </div>
              <div className="inline-block px-5 py-2 bg-primary/30 rounded-full text-white text-sm font-bold uppercase tracking-wider shadow-lg">
                From Zero to Turnkey
              </div>
            </div>
            
            <p className="text-xl text-white/95 leading-relaxed mb-8 max-w-4xl mx-auto">
              Professional climbing gym construction company providing turnkey bouldering gym builds from initial concept to final handover. 
              Our in-house team handles commercial climbing wall design, CNC manufacturing, professional installation, and facility commissioning—ensuring 
              seamless coordination and world-class climbing facility results across Europe.
            </p>
            
            <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
              {[
                "Custom 3D design & engineering",
                "CNC precision manufacturing", 
                "Professional installation on-site",
                "Safety certification support"
              ].map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-all duration-300 group/item"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-2 h-2 rounded-full bg-primary group-hover/item:scale-150 transition-transform" />
                  <span className="text-white/90 font-medium group-hover/item:text-white transition-colors">{feature}</span>
                </div>
              ))}
            </div>
            
            <div className="mt-8 flex justify-center">
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" className="text-lg px-8 py-6 shadow-xl hover:shadow-2xl">
                    Contact Us
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-display">Get in Touch</DialogTitle>
                  </DialogHeader>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mt-4">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Your name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="your@email.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone</FormLabel>
                            <FormControl>
                              <Input placeholder="+371 12345678" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Message</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="Tell us about your project..." 
                                className="min-h-[120px]"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                        {isSubmitting ? "Sending..." : "Send Message"}
                      </Button>
                    </form>
                  </Form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
      
      {/* Supporting Services - Compact Grid */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            <a
              href="https://emotion-holds.com"
              target="_blank"
              rel="noopener noreferrer"
              className="p-8 bg-card border-2 border-border rounded-2xl hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-2 group animate-fade-in cursor-pointer"
              style={{ animationDelay: '200ms' }}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-primary/10 rounded-xl group-hover:bg-primary/20 transition-colors">
                  <Package className="w-7 h-7 text-primary" />
                </div>
                <h4 className="font-display text-xl text-foreground">Holds & Equipment</h4>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                We manufacture climbing holds, macros, volumes and other climbing equipment from our own production
              </p>
            </a>
            
            <div 
              className="p-8 bg-card border-2 border-border rounded-2xl hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-2 group animate-fade-in"
              style={{ animationDelay: '300ms' }}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-primary/10 rounded-xl group-hover:bg-primary/20 transition-colors">
                  <Smartphone className="w-7 h-7 text-primary" />
                </div>
                <h4 className="font-display text-xl text-foreground">Gym Systems</h4>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Access control, booking software, and management platforms
              </p>
            </div>
            
            <div 
              className="p-8 bg-card border-2 border-border rounded-2xl hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-2 group animate-fade-in"
              style={{ animationDelay: '400ms' }}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-primary/10 rounded-xl group-hover:bg-primary/20 transition-colors">
                  <Wrench className="w-7 h-7 text-primary" />
                </div>
                <h4 className="font-display text-xl text-foreground">Support</h4>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Ongoing maintenance, consultation, and technical assistance
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;